/* eslint no-console: 0 */
/* global axios */
import ApiClient from '../ApiClient';

const isHttpUrl = value =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

const addButtonsComponents = params => {
  if (params.processed_params) {
    const buttons = [];

    Object.entries(params.processed_params).forEach(([key, value]) => {
      const urlMatch = key.match(/^BUTTON_(\d+)$/);
      if (urlMatch) {
        buttons.push({
          type: 'button',
          sub_type: 'url',
          index: urlMatch[1],
          parameters: [
            {
              type: 'text',
              text: value,
            },
          ],
        });
        delete params.processed_params[key];
        return;
      }

      const copyCodeMatch = key.match(/^COPY_CODE_BUTTON_(\d+)$/);
      if (copyCodeMatch) {
        buttons.push({
          type: 'button',
          sub_type: 'copy_code',
          index: copyCodeMatch[1],
          parameters: [
            {
              type: 'coupon_code',
              coupon_code: value,
            },
          ],
        });
        delete params.processed_params[key];
      }
    });

    const existingButtons = Array.isArray(params.button_components) ? params.button_components : [];
    params.button_components = [...existingButtons, ...buttons];
  }
};

const addHeaderComponents = params => {
  if (params.processed_params) {
    if (params.header_media?.type) {
      const mediaType = params.header_media.type;
      const mediaLink =
        params.header_media.link ||
        params.header_media.file_url ||
        params.header_media.fileUrl;
      const mediaPayload = {
        blob_id: params.header_media.blob_id,
        blob_key: params.header_media.blob_key,
        mime_type: params.header_media.mime_type,
        filename: params.header_media.filename,
        byte_size: params.header_media.byte_size,
      };

      if (mediaLink) {
        mediaPayload.link = mediaLink;
        mediaPayload.file_url = mediaLink;
      }

      const hasUsableMediaPayload = Object.values(mediaPayload).some(
        value => value !== undefined && value !== null && value !== ''
      );

      if (hasUsableMediaPayload) {
        params.header_components = {
          type: mediaType,
          [mediaType]: {
            ...mediaPayload,
          },
        };
      }
    } else if (params.processed_params.VIDEO && isHttpUrl(params.processed_params.VIDEO)) {
      params.header_components = {
        type: 'video',
        video: {
          link: params.processed_params.VIDEO,
        },
      };
    } else if (params.processed_params.IMAGE && isHttpUrl(params.processed_params.IMAGE)) {
      params.header_components = {
        type: 'image',
        image: {
          link: params.processed_params.IMAGE,
        },
      };
    } else if (params.processed_params.DOCUMENT && isHttpUrl(params.processed_params.DOCUMENT)) {
      params.header_components = {
        type: 'document',
        document: {
          link: params.processed_params.DOCUMENT,
        },
      };
    } else {
      const headerKeys = Object.keys(params.processed_params).filter(key => key.startsWith('TEXT_'));

      if (headerKeys.length > 0) {
        const headerKey = headerKeys[0];
        const paramId = headerKey.slice(5);
        const value = params.processed_params[headerKey];

        if (/^\d+$/.test(paramId)) {
          params.header_components = {
            type: 'text',
            text: value
          };
        } else {
          params.header_components = {
            type: 'text',
            text: value,
            parameter_name: paramId
          };
        }
      }
    }
  }
};

export const normalizeTemplateParams = templateParams => {
  if (!templateParams) return templateParams;

  const normalizedParams = JSON.parse(JSON.stringify(templateParams));
  addHeaderComponents(normalizedParams);
  addButtonsComponents(normalizedParams);

  if (normalizedParams.processed_params) {
    if (normalizedParams.processed_params.DOCUMENT) {
      delete normalizedParams.processed_params.DOCUMENT;
    }
    if (normalizedParams.processed_params.IMAGE) {
      delete normalizedParams.processed_params.IMAGE;
    }
    if (normalizedParams.processed_params.VIDEO) {
      delete normalizedParams.processed_params.VIDEO;
    }
    if (normalizedParams.processed_params.TEXT) {
      delete normalizedParams.processed_params.TEXT;
    }

    Object.keys(normalizedParams.processed_params).forEach(key => {
      if (key.startsWith('TEXT_')) {
        delete normalizedParams.processed_params[key];
      }
    });
  }

  return normalizedParams;
};

export const buildCreatePayload = ({
  message,
  isPrivate,
  contentType,
  contentAttributes,
  additionalAttributes,
  echoId,
  files,
  ccEmails = '',
  bccEmails = '',
  toEmails = '',
  templateParams,
  messengerTemplateParams,
}) => {
  let payload;

  if (files && files.length !== 0) {
    payload = new FormData();
    if (contentType) {
      payload.append('content_type', contentType);
    }
    if (message) {
      payload.append('content', message);
    }
    files.forEach(file => {
      payload.append('attachments[]', file);
    });
    payload.append('private', isPrivate);
    payload.append('echo_id', echoId);
    payload.append('cc_emails', ccEmails);
    payload.append('bcc_emails', bccEmails);
    if (toEmails) {
      payload.append('to_emails', toEmails);
    }
    if (contentAttributes) {
      payload.append('content_attributes', JSON.stringify(contentAttributes));
    }
    if (additionalAttributes) {
      payload.append('additional_attributes', JSON.stringify(additionalAttributes));
    }
    if (templateParams) {
      payload.append('template_params', JSON.stringify(normalizeTemplateParams(templateParams)));
    }
    if (messengerTemplateParams) {
      payload.append(
        'messenger_template_params',
        JSON.stringify(messengerTemplateParams)
      );
    }
  } else {
    const normalizedTemplateParams = normalizeTemplateParams(templateParams);
    payload = {
      content_type: contentType,
      content: message,
      private: isPrivate,
      echo_id: echoId,
      content_attributes: contentAttributes,
      additional_attributes: additionalAttributes,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
      to_emails: toEmails,
      template_params: normalizedTemplateParams,
      messenger_template_params: messengerTemplateParams,
    };
  }
  return payload;
};

class MessageApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  create({
    conversationId,
    message,
    private: isPrivate,
    contentType,
    contentAttributes,
    additionalAttributes,
    echo_id: echoId,
    files,
    ccEmails = '',
    bccEmails = '',
    toEmails = '',
    templateParams,
    messengerTemplateParams,
  }) {
    return axios({
      method: 'post',
      url: `${this.url}/${conversationId}/messages`,
      data: buildCreatePayload({
        message,
        isPrivate,
        contentType,
        contentAttributes,
        additionalAttributes,
        echoId,
        files,
        ccEmails,
        bccEmails,
        toEmails,
        templateParams,
        messengerTemplateParams,
      }),
    });
  }

  delete(conversationID, messageId) {
    return axios.delete(`${this.url}/${conversationID}/messages/${messageId}`);
  }

  retry(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/retry`
    );
  }

  getPreviousMessages({ conversationId, after, before }) {
    const params = { before };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  getPreviousMessagesByContactId({ conversationId, after, before,contactId }) {
    const params = { before, contactId };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  translateMessage(conversationId, messageId, targetLanguage) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/translate`,
      {
        target_language: targetLanguage,
      }
    );
  }

  getRecordingUrl(conversationId, messageId) {
    return axios.get(
      `${this.url}/${conversationId}/messages/${messageId}/recording_url`
    );
  }

  sendReaction({ conversationId, emoji, messageId }) {
    return axios.post(`${this.url}/${conversationId}/reactions`, {
      emoji,
      message_id: messageId,
    });
  }
}

export default new MessageApi();
